import { useEffect, useState } from 'react';
import Modal from '../components/Modal.jsx';
import { apiTimeOffs, apiTimeOffTypes } from '../api/client.js';
import { halfDayLabel } from '../components/HalfDayPicker.jsx';
import { fullDayLabel, parseIso } from './dateUtils.js';

/**
 * #106 — Indisponibilité d'un sous-traitant depuis l'app native : déclaration
 * (`mode: 'create'`) ou suppression (`mode: 'delete'`, avec confirmation).
 *
 * Mêmes appels que « Mes congés » sur le web :
 *  - GET    /time-off-types          (id du type « Indisponibilité ») ;
 *  - POST   /timeoffs                (validée d'office par le serveur) ;
 *  - DELETE /timeoffs/:id.
 * Réservé aux sous-traitants (vérifié par l'appelant) ; le serveur reste
 * l'autorité : en jeton mobile, seul ce type est accepté, pour soi.
 * Pas de récurrence en mobile.
 */

// Miroir de `TIMEOFF_TYPE_UNAVAILABILITY` côté API.
export const UNAVAILABILITY_TYPE_NAME = 'Indisponibilité';

/** Indisponibilité supprimable depuis l'app : la sienne, saisie dans Synolog. */
export function isOwnUnavailability(timeOff) {
  return (
    timeOff?.timeOffType?.name === UNAVAILABILITY_TYPE_NAME &&
    !timeOff.gescomAbsenceId &&
    !timeOff.isRecurring
  );
}

// Un seul jour : journée, matin ou après-midi (bornes AM/PM de l'API).
const SINGLE_DAY = [
  { value: 'full', label: 'Journée', startHalf: 'AM', endHalf: 'PM' },
  { value: 'morning', label: 'Matin', startHalf: 'AM', endHalf: 'AM' },
  { value: 'afternoon', label: 'Après-midi', startHalf: 'PM', endHalf: 'PM' },
];
// Plusieurs jours : départ le matin ou l'après-midi, retour à midi ou le soir.
const START_HALVES = [
  { value: 'AM', label: 'Matin' },
  { value: 'PM', label: 'Après-midi' },
];
const END_HALVES = [
  { value: 'AM', label: 'Midi' },
  { value: 'PM', label: 'Soir' },
];

/** Libellé court d'une période : « lun. 6 oct. » ou « du … au … ». */
function periodLabel(timeOff) {
  const start = String(timeOff.startDate).slice(0, 10);
  const end = String(timeOff.endDate).slice(0, 10);
  const base =
    start === end
      ? fullDayLabel(parseIso(start))
      : `Du ${fullDayLabel(parseIso(start))} au ${fullDayLabel(parseIso(end))}`;
  const half = halfDayLabel(timeOff);
  return half ? `${base} (${half})` : base;
}

export default function MobileUnavailabilityModal({
  open,
  mode, // 'create' | 'delete'
  timeOff, // instance issue de /schedule (mode delete)
  defaultDate, // ISO (mode create)
  personId,
  onClose,
  onSaved,
}) {
  const [typeId, setTypeId] = useState(null);
  const [typeError, setTypeError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [singleHalf, setSingleHalf] = useState('full');
  const [startHalf, setStartHalf] = useState('AM');
  const [endHalf, setEndHalf] = useState('PM');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStartDate(defaultDate ?? '');
    setEndDate(defaultDate ?? '');
    setSingleHalf('full');
    setStartHalf('AM');
    setEndHalf('PM');
    setNotes('');
    setSubmitting(false);
    setError(null);
  }, [open, defaultDate]);

  // Id du type « Indisponibilité », lu une fois à la première ouverture.
  useEffect(() => {
    if (!open || mode !== 'create' || typeId) return undefined;
    let cancelled = false;
    apiTimeOffTypes
      .list('active')
      .then(({ items }) => {
        if (cancelled) return;
        const type = (items ?? []).find((t) => t.name === UNAVAILABILITY_TYPE_NAME);
        setTypeId(type?.id ?? null);
        setTypeError(type ? null : 'Type « Indisponibilité » introuvable.');
      })
      .catch((e) => {
        if (!cancelled) setTypeError(e?.message || 'Types de congé indisponibles.');
      });
    return () => {
      cancelled = true;
    };
  }, [open, mode, typeId]);

  if (!open) return null;

  const singleDay = Boolean(startDate) && startDate === endDate;
  const close = submitting ? () => {} : onClose;

  const save = async () => {
    if (!startDate || !endDate) {
      setError('Renseignez les dates.');
      return;
    }
    if (endDate < startDate) {
      setError('La date de fin précède la date de début.');
      return;
    }
    const halves = singleDay
      ? SINGLE_DAY.find((h) => h.value === singleHalf)
      : { startHalf, endHalf };
    setSubmitting(true);
    setError(null);
    try {
      await apiTimeOffs.create({
        personIds: [personId],
        timeOffTypeId: typeId,
        startDate,
        endDate,
        hoursPerDay: 0,
        fullDay: true,
        startHalf: halves.startHalf,
        endHalf: halves.endHalf,
        notes: notes.trim() || null,
        recurrenceRule: null,
      });
      onSaved('Indisponibilité enregistrée.');
    } catch (e) {
      setError(e?.message || 'Enregistrement impossible.');
      setSubmitting(false);
    }
  };

  const remove = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiTimeOffs.archive(timeOff.id);
      onSaved('Indisponibilité supprimée.');
    } catch (e) {
      setError(e?.message || 'Suppression impossible.');
      setSubmitting(false);
    }
  };

  if (mode === 'delete') {
    return (
      <Modal
        open
        onClose={close}
        title="Supprimer l’indisponibilité"
        subtitle={timeOff ? periodLabel(timeOff) : undefined}
        size="sm"
        footer={
          <div className="m-modal-footer">
            <span className="m-modal-footer__spacer" />
            <button type="button" className="m-btn" onClick={close} disabled={submitting}>
              Annuler
            </button>
            <button
              type="button"
              className="m-btn m-btn--danger"
              onClick={remove}
              disabled={submitting}
            >
              {submitting ? 'Suppression…' : 'Supprimer'}
            </button>
          </div>
        }
      >
        <div className="m-form">
          <span className="m-block__sub">Vous serez de nouveau affiché disponible sur cette période.</span>
          {error ? (
            <div className="m-field__error" role="alert">
              {error}
            </div>
          ) : null}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={close}
      title="Déclarer une indisponibilité"
      size="sm"
      footer={
        <div className="m-modal-footer">
          <span className="m-modal-footer__spacer" />
          <button type="button" className="m-btn" onClick={close} disabled={submitting}>
            Annuler
          </button>
          <button
            type="button"
            className="m-btn m-btn--primary"
            onClick={save}
            disabled={submitting || !typeId}
          >
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      }
    >
      <div className="m-form">
        <div className="m-field-row">
          <label className="m-field">
            <span className="m-field__label">Du</span>
            <input
              className="m-input"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (!endDate || e.target.value > endDate) setEndDate(e.target.value);
              }}
            />
          </label>
          <label className="m-field">
            <span className="m-field__label">Au</span>
            <input
              className="m-input"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
        </div>

        {singleDay ? (
          <div className="m-field">
            <span className="m-field__label">Période</span>
            <div className="m-pills">
              {SINGLE_DAY.map((h) => (
                <button
                  key={h.value}
                  type="button"
                  className="m-pill"
                  data-active={singleHalf === h.value}
                  onClick={() => setSingleHalf(h.value)}
                >
                  {h.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="m-field">
              <span className="m-field__label">Début</span>
              <div className="m-pills">
                {START_HALVES.map((h) => (
                  <button
                    key={h.value}
                    type="button"
                    className="m-pill"
                    data-active={startHalf === h.value}
                    onClick={() => setStartHalf(h.value)}
                  >
                    {h.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="m-field">
              <span className="m-field__label">Fin</span>
              <div className="m-pills">
                {END_HALVES.map((h) => (
                  <button
                    key={h.value}
                    type="button"
                    className="m-pill"
                    data-active={endHalf === h.value}
                    onClick={() => setEndHalf(h.value)}
                  >
                    {h.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <label className="m-field">
          <span className="m-field__label">Note (facultative)</span>
          <textarea
            className="m-input"
            rows={2}
            maxLength={10000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        {typeError || error ? (
          <div className="m-field__error" role="alert">
            {error || typeError}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
